import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useHandler } from "@redotech/react-util/hook";
import {
  useCloseConversationAction,
  useReopenConversationAction,
  type ConversationAction,
} from "../actions/use-conversation-actions";

export interface UseConversationActionToolbarResult {
  /** The primary close/reopen action based on current conversation status */
  primaryAction: ConversationAction;
  /** The conversation context for isEnabled/isVisible checks */
  conversation: ConversationListItemResponse;
  /** Execute the primary action */
  handlePrimaryAction: () => void;
}

/**
 * Hook that provides the primary conversation action for a toolbar.
 * The full action menu is handled by ConversationActionsDropdown.
 */
export function useConversationActionToolbar(
  conversationId: ConversationId,
  conversation: ConversationListItemResponse,
): UseConversationActionToolbarResult {
  const closeAction = useCloseConversationAction();
  const reopenAction = useReopenConversationAction();

  const isClosed = conversation.status === "CLOSED";
  const primaryAction = isClosed ? reopenAction : closeAction;

  const handlePrimaryAction = useHandler(() => {
    const isEnabled = primaryAction.isEnabled?.(conversation) ?? true;
    if (isEnabled) {
      void primaryAction.execute(conversationId);
    }
  });

  return { primaryAction, conversation, handlePrimaryAction };
}
